import type { QuotationDocumentData } from '@/quotation/document/types';
import { getLabels } from '@/quotation/document/labels';
import { formatDateShort } from '@/quotation/document/formatters';

interface Props { data: QuotationDocumentData }

function HeaderMeta({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-end gap-3">
      <span className="text-xs text-neutral-400 self-center whitespace-nowrap">{label}</span>
      <span className={`text-sm text-neutral-800 w-[140px] text-right font-medium ${mono ? 'font-mono' : ''}`}>
        {value}
      </span>
    </div>
  );
}

export default function HtmlDocHeader({ data }: Props) {
  const L = getLabels(data.documentLocale);
  const title = L.documentTitle[data.documentType];

  return (
    <div className="px-8 md:px-12 pt-6 md:pt-7">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6">
        {/* Brand */}
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-ink-700 flex items-center justify-center shrink-0">
            <i className="ri-box-3-line text-white text-xl" />
          </div>
          <div>
            <p className="text-xl font-extrabold tracking-tight text-ink-700 leading-tight">
              {data.seller.companyName}
            </p>
            {data.seller.legalName && (
              <p className="text-xs text-neutral-500 mt-0.5 uppercase tracking-wider">
                {data.seller.legalName}
              </p>
            )}
          </div>
        </div>

        {/* Document meta — aligned label/value columns */}
        <div className="sm:text-right">
          <h2 className="text-lg md:text-xl font-extrabold text-ink-700 tracking-widest uppercase leading-tight">
            {title}
          </h2>
          <div className="mt-3 space-y-1.5">
            <HeaderMeta label={L.quotationNo} value={data.quotationNumber} mono />
            {data.referenceNumber && (
              <HeaderMeta label={L.referenceNo} value={data.referenceNumber} mono />
            )}
            <HeaderMeta
              label={L.date}
              value={formatDateShort(data.issuedAt, data.documentLocale)}
            />
            {data.validUntil && (
              <HeaderMeta
                label={L.validUntil}
                value={formatDateShort(data.validUntil, data.documentLocale)}
              />
            )}
            <HeaderMeta label={L.currency} value={data.currency} />
          </div>
        </div>
      </div>

      <div className="mt-4 border-t-2 border-ink-700" />
    </div>
  );
}